import { useRef, useCallback, useState, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import { IconButton } from "../atoms";

const style = {
  border: "1px dashed gray",
  borderRadius: "3px",
  padding: "0.5rem 1rem",
  marginBottom: ".5rem",
  backgroundColor: "white",
  cursor: "move",
  display: "grid",
  gridTemplateColumns: "auto auto auto",
};

const badgeStyle = {
  color: "white",
  position: "absolute",
  right: "4px",
  top: "5px",
  border: "1px solid white",
  padding: "1px 4px",
  borderRadius: "50%",
  lineHight: "10px",
  background: "#E3031F",
  fontSize: "7px",
  fontWeight: "700",
};

export const Card = ({ id, src, index, moveCard, deleteImage }) => {
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: "card",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: "card",
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  return (
    <div
      ref={ref}
      style={{ opacity, paddingBottom: "10px" }}
      data-handler-id={handlerId}
    >
      <div className={"secondIndexImage"}>
        <div className={"overlay"} />
        <span className="imageNumber" style={badgeStyle}>
          {index + 1}
        </span>
        <img src={src} className="formImg" alt="" />
        <IconButton onClick={deleteImage} className="imageRemoveCls" />
      </div>
    </div>
  );
};

const updateList = (prevCards, dragIndex, hoverIndex) => {
  const updatedCards = [...prevCards];
  const draggedCard = updatedCards.splice(dragIndex, 1)[0];
  updatedCards.splice(hoverIndex, 0, draggedCard);
  return updatedCards;
};

export const Container = ({ dragData, onImageRemove, onImageChangeUpdate }) => {
  const [images, setCards] = useState([]);

  useEffect(() => {
    setCards(dragData);
  }, [dragData]);

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      setCards((prevCards) => {
        const newList = updateList(prevCards, dragIndex, hoverIndex);
        onImageChangeUpdate(newList);
        return newList;
      });
    },
    [onImageChangeUpdate]
  );

  const renderCard = useCallback(
    (item, index) => {
      return (
        <Card
          key={item.index}
          index={index}
          id={index}
          src={item.src}
          moveCard={moveCard}
          deleteImage={() => onImageRemove(index)}
        />
      );
    },
    [moveCard, onImageRemove]
  );
  return images.length !== 0 ? (
    <>
      <div style={style}>{images.map((item, i) => renderCard(item, i))}</div>
    </>
  ) : (
    ""
  );
};
